import { FileText, ImageIcon, Trash2, Upload } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';
import { MAX_ATTACHMENT_BYTES } from '@cfl/assets-shared';

import { Button } from '@/components/cargofl-ui';
import { DeleteRecordDialog } from '@/components/bulk-delete';
import { useLightbox } from '@/components/lightbox';
import { Hint } from '@/components/cargofl-ui';
import { ApiError } from '@/lib/api';
import {
  documentUrl,
  useDeleteDocument,
  useDocuments,
  useUploadDocument,
  type AssetDocument,
  type DocumentEntity,
} from '@/lib/documents-api';

/**
 * Files attached to an asset or an exit record.
 *
 * The invoice for a laptop, the signed clearance for a leaver. One component for both,
 * because the difference is only the noun in the copy - the rules about what may be
 * uploaded are the API's, and duplicating the UI would mean fixing every bug twice.
 *
 * There is no drag-and-drop. It looks impressive in a demo and is invisible on a
 * phone, which is where a photographed clearance form is most likely to come from;
 * a plain file input opens the camera there and costs nothing.
 */
export function Documents({
  entity,
  entityId,
  canManage,
  /** "invoice" / "clearance document" - used in the empty state and the hint. */
  noun,
  emptyHint,
}: {
  entity: DocumentEntity;
  entityId: string;
  canManage: boolean;
  noun: string;
  emptyHint: string;
}) {
  const { data: documents, isPending } = useDocuments(entity, entityId);
  const upload = useUploadDocument(entity, entityId);
  const remove = useDeleteDocument(entity, entityId);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [deleting, setDeleting] = React.useState<AssetDocument | null>(null);

  // Only the images. A PDF invoice has nothing to show in a slider and keeps opening
  // in a tab, where the browser's own reader handles it.
  const lightbox = useLightbox(
    (documents ?? [])
      .filter((document) => document.mimeType.startsWith('image/'))
      .map((document) => ({
        id: document.id,
        url: documentUrl(document.id),
        filename: document.filename,
      })),
  );

  const onPick = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset immediately, so picking the SAME file again after a failure still fires
    // a change event. Without this, a retry of the identical file does nothing.
    event.target.value = '';
    if (!file) return;

    // Checked here as a courtesy so a 12 MB photo fails instantly rather than after
    // the upload. The API's limit is the one that counts.
    if (file.size > MAX_ATTACHMENT_BYTES) {
      toast.error(
        `That file is ${(file.size / 1024 / 1024).toFixed(1)} MB. The limit is ${Math.round(
          MAX_ATTACHMENT_BYTES / 1024 / 1024,
        )} MB.`,
      );
      return;
    }

    try {
      await upload.mutateAsync(file);
      toast.success(`${file.name} attached.`);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'That file could not be attached.');
    }
  };

  return (
    <div className="space-y-3">
      {isPending ? (
        <p className="text-xs text-muted-foreground">Loading files…</p>
      ) : documents && documents.length > 0 ? (
        <ul className="divide-y rounded-md border">
          {documents.map((document) => (
            <li key={document.id} className="flex items-center gap-3 px-3 py-2">
              {document.mimeType.startsWith('image/') ? (
                <ImageIcon className="size-4 shrink-0 text-muted-foreground" />
              ) : (
                <FileText className="size-4 shrink-0 text-muted-foreground" />
              )}

              <div className="min-w-0 flex-1">
                {document.mimeType.startsWith('image/') ? (
                  <Hint label={'Open this document'}><button
                    type="button"
                    onClick={() => lightbox.open(document.id)}
                    className="block max-w-full truncate text-left text-sm font-medium hover:underline"
                  >
                    {document.filename}
                  </button></Hint>
                ) : (
                  <a
                    href={documentUrl(document.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="block truncate text-sm font-medium hover:underline"
                  >
                    {document.filename}
                  </a>
                )}
                <p className="truncate text-[11px] text-muted-foreground">
                  <span className="at-num">{formatSize(document.sizeBytes)}</span>
                  {document.uploadedBy ? ` · ${document.uploadedBy.name}` : ''}
                  {' · '}
                  <span className="at-num">{document.createdAt.slice(0, 10)}</span>
                </p>
              </div>

              {canManage && (
                <Button hint={"Remove this document"}
                  size="sm"
                  variant="ghost"
                  aria-label={`Remove ${document.filename}`}
                  onClick={() => setDeleting(document)}
                >
                  <Trash2 className="size-4" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="rounded-md border border-dashed px-3 py-6 text-center text-xs text-muted-foreground">
          {emptyHint}
        </p>
      )}

      {canManage && (
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
            onChange={(event) => void onPick(event)}
          />
          <Button hint={"Upload a document (invoice, warranty, photo…)"}
            size="sm"
            variant="secondary"
            disabled={upload.isPending}
            onClick={() => inputRef.current?.click()}
          >
            <Upload className="mr-1.5 size-4" />
            {upload.isPending ? 'Uploading…' : `Attach ${noun}`}
          </Button>
          <span className="text-[11px] text-muted-foreground">
            PDF or photo, up to {Math.round(MAX_ATTACHMENT_BYTES / 1024 / 1024)} MB
          </span>
        </div>
      )}

      {lightbox.element}

      {deleting && (
        <DeleteRecordDialog
          open
          onOpenChange={(open) => !open && setDeleting(null)}
          label={deleting.filename}
          noun="file"
          alternative="it stays where it is."
          onDelete={async () => {
            await remove.mutateAsync(deleting.id);
            setDeleting(null);
          }}
        />
      )}
    </div>
  );
}

/** Bytes as something a person reads, not a number they have to divide. */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
